import {
    F_MapMarkerDirectionFragment,
    F_MapMarkerImageFragment,
    F_MapMarkerIndoorStandFragment,
    F_MapMarkerLabelFragment,
    F_MapMarkerLineFragment,
    F_MapMarkerPolygonFragment,
    F_MapMarkerRouteFragment,
    F_MapMarkerTextFragment,
    F_MapMarkerVariantIconFragment,
    F_MapMarkerWallFragment,
    MapMarkerWallStructureType
} from '__generated__v2/graphql';
import { orderBy } from 'lodash';

export type F_MapMarkerFragment =
    | F_MapMarkerIndoorStandFragment
    | F_MapMarkerDirectionFragment
    | F_MapMarkerRouteFragment
    | F_MapMarkerLineFragment
    | F_MapMarkerPolygonFragment
    | F_MapMarkerWallFragment
    | F_MapMarkerVariantIconFragment
    | F_MapMarkerImageFragment
    | F_MapMarkerTextFragment
    | F_MapMarkerLabelFragment;

export interface MarkerGroupMarker {
    type: 'marker';
    marker: F_MapMarkerFragment;
    id: string;
}

export function isWallGroupable(marker: F_MapMarkerWallFragment) {
    if (marker.structureType === MapMarkerWallStructureType.Floor) {
        return false;
    }
    if (marker.wallOpacity && marker.wallOpacity < 1) {
        return false;
    }
    if (marker.fillOpacity && marker.fillOpacity < 1) {
        return false;
    }
    return true;
}

type MarkerGroup =
    | { type: 'wallGroup'; markers: F_MapMarkerWallFragment[]; id: string }
    | MarkerGroupMarker;

export interface DividedMarkers {
    variantIconMarkers: F_MapMarkerVariantIconFragment[];
    floorMarkers: F_MapMarkerWallFragment[];
    markers: MarkerGroup[];
    indoorStands: F_MapMarkerIndoorStandFragment[];
    labelMarkers: F_MapMarkerLabelFragment[];
}

export function divideMarkers(
    markers: F_MapMarkerFragment[],
    opts: { isEditMode: boolean; selectedIds: string[] }
): DividedMarkers {
    const nonGroupMarkers = orderBy(
        markers.filter((e) => {
            return (
                e.__typename !== 'MapMarkerVariantIcon' &&
                e.__typename !== 'MapMarkerIndoorStand' &&
                e.__typename !== 'MapMarkerLabel' &&
                !(
                    e.__typename === 'MapMarkerWall' &&
                    e.structureType === MapMarkerWallStructureType.Floor
                )
            );
        }),
        'order',
        'desc'
    );

    const indoorStands = markers.filter(
        (e): e is F_MapMarkerIndoorStandFragment => e.__typename === 'MapMarkerIndoorStand'
    );

    const labelMarkers = markers.filter(
        (e): e is F_MapMarkerLabelFragment => e.__typename === 'MapMarkerLabel'
    );

    const variantIconMarkers = orderBy(
        markers.filter(
            (e): e is F_MapMarkerVariantIconFragment => e.__typename === 'MapMarkerVariantIcon'
        ),
        'order',
        'desc'
    );

    const floorMarkers = markers.filter<F_MapMarkerWallFragment>(
        (e): e is F_MapMarkerWallFragment =>
            e.__typename === 'MapMarkerWall' &&
            (e.structureType === MapMarkerWallStructureType.Floor ||
                (e.structureType === MapMarkerWallStructureType.Wall && e.wallIncludeFloor))
    );

    const markerGroups: MarkerGroup[] = [];
    let currentWallGroup: F_MapMarkerWallFragment[] = [];
    let index = 0;
    for (let i = 0; i < nonGroupMarkers.length; i++) {
        const m = nonGroupMarkers[i];
        if (
            m.__typename === 'MapMarkerWall' &&
            !opts.selectedIds.includes(m.id) &&
            isWallGroupable(m)
        ) {
            currentWallGroup.push(m);
        } else {
            if (currentWallGroup.length > 0) {
                markerGroups.push({
                    type: 'wallGroup',
                    markers: currentWallGroup,
                    id: 'wall_group|' + index.toString()
                });
                index += 1;
                currentWallGroup = [];
            }
            markerGroups.push({ type: 'marker', marker: m, id: m.id });
        }
    }

    if (currentWallGroup.length > 0) {
        markerGroups.push({
            type: 'wallGroup',
            markers: currentWallGroup,
            id: index.toString()
        });
    }

    return {
        floorMarkers,
        markers: markerGroups,
        variantIconMarkers,
        indoorStands,
        labelMarkers
    };
}
